// Top-level App shell. Two top-level layouts:
//
//   - "home" — the Friends + DMs hub. Mounted when the leftmost
//     icon is clicked (the strawberry cloud-bubble). The channel
//     sidebar shows a Friends pseudo-row + the DM list; the main
//     pane shows either the Friends view or an active DM.
//
//   - "space" — a real Space (Personal Workspace, Shared
//     Workspace, or Community). Channel sidebar shows the Space's
//     channel list; main pane shows the active channel's content.
//     Personal Workspaces additionally have an `overview` channel
//     that renders the Personal Dashboard (notes, tasks, snippets,
//     files) — equivalent to the FigTime "main dashboard" the user
//     called out as missing.
//
// The full ProfileModal is mounted at this level so it sits above
// every other surface; the same modal is opened by the user-card
// avatar (via ProfilePopover) AND the dashboard avatar.

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Menu, Sparkles, UserRound, Users, X } from "lucide-react";

import { TooltipProvider, cn } from "@link/ui";

import { AuthScreen } from "./components/auth/AuthScreen";
import { DeviceLinkPage } from "./components/auth/DeviceLink";
import { StaffApproval } from "./components/auth/StaffApproval";
import { DesktopStaffApproval } from "./components/auth/DesktopStaffApproval";
import { ChannelSidebar } from "./components/ChannelSidebar";
import { FriendsView } from "./components/FriendsView";
import { MainPane } from "./components/MainPane";
import { MediaDock } from "./components/media/MediaDock";
import { MemberList } from "./components/MemberList";
import { ProfileModal } from "./components/ProfileModal";
import { PrivacyPage } from "./components/public/PrivacyPage";
import { TermsPage } from "./components/public/TermsPage";
import { TransparencyPage } from "./components/public/TransparencyPage";
import { SearchDialog } from "./components/SearchDialog";
import { SettingsModal } from "./components/SettingsModal";
import { ServerRail } from "./components/ServerRail";
import { UploadToasts } from "./components/UploadToasts";
import { ReleaseChangelog } from "./components/ReleaseChangelog";
import { readSessionToken } from "./lib/http";
import { api } from "./lib/api";
import {
  OPEN_DIRECT_MESSAGE_EVENT,
  type OpenDirectMessageDetail,
} from "./lib/dm-navigation";
import { useChannels, useDms, useFriends, useSpaces } from "./state/data";
import { useSelf } from "./state/SelfSession";
import type {
  AppView,
  ChannelView,
  HomeTab,
  SpaceView,
} from "./types/ui";

export function App() {
  // Public route dispatcher. The legal/transparency surfaces are
  // unauthenticated and reachable directly via URL — they short-
  // circuit the rest of the app shell.
  const path =
    typeof window === "undefined" ? "/" : window.location.pathname;
  if (path === "/transparency") return <TransparencyPage />;
  if (path === "/legal/privacy") return <PrivacyPage />;
  if (path === "/legal/terms") return <TermsPage />;
  if (path === "/link-device") return <DeviceLinkPage />;
  if (path === "/staff-approval") return <StaffApproval />;
  // Staff operations live in a separately built and deployed application.
  // The public bundle has only identity approval, never staff operations.
  if (path === "/admin" || path.startsWith("/admin/") || path === "/operations") {
    return <PublicNotFound />;
  }

  const inviteMatch = /^\/invite\/([^/]+)$/.exec(path);
  return <AppShell inviteToken={inviteMatch?.[1] ?? null} />;
}

function PublicNotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app-canvas px-6 text-cloud-700">
      <div className="max-w-md text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cloud-400">404</p>
        <h1 className="mt-3 text-3xl font-semibold text-cloud-800">Nothing is here.</h1>
        <p className="mt-3 text-sm text-cloud-500">The page you opened is not part of Link.</p>
        <a href="/" className="mt-6 inline-flex rounded-xl bg-strawberry-gradient px-4 py-2.5 text-sm font-semibold text-white">
          Return to Link
        </a>
      </div>
    </div>
  );
}

function AppShell({ inviteToken }: { inviteToken: string | null }) {
  const self = useSelf();

  // Default landing: the home hub. The user's Personal Workspace is
  // now a separate Space accessible from the rail (the second icon),
  // not the home button.
  const [view, setView] = useState<AppView>(() => {
    if (typeof window === "undefined") return { kind: "home", tab: "friends" };
    const params = new URLSearchParams(window.location.search);
    const spaceXid = params.get("space")?.trim() ?? "";
    const channelXid = params.get("channel")?.trim() ?? "";
    return spaceXid.length > 0 && channelXid.length > 0
      ? { kind: "space", spaceXid, channelXid }
      : { kind: "home", tab: "friends" };
  });
  const [lastHomeTab, setLastHomeTab] = useState<HomeTab>("friends");
  const [activeDmXid, setActiveDmXid] = useState<string | null>(null);
  const [dmDrafts, setDmDrafts] = useState<
    Record<string, { body: string; sendImmediately: boolean }>
  >({});
  const [dmError, setDmError] = useState<string | null>(null);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const handledInvite = useRef<string | null>(null);
  // Per Req 18.4 the member list is a togglable panel. Default to
  // showing it; the channel header's people icon flips it.
  const [showMembers, setShowMembers] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileNavOpen]);
  // Auth screen open state. Defaults open if the URL says so
  // (e.g. /?auth=1 from a marketing link), and toggleable from
  // the user card.
  const [authOpen, setAuthOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      new URLSearchParams(window.location.search).has("auth") ||
      readSessionToken() === null
    );
  });
  // Global Cmd/Ctrl-K search palette.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Live data hooks. The application shell is only mounted after real
  // authentication, so these surfaces never substitute demo accounts.
  const spacesData = useSpaces();
  const refreshSpaces = spacesData.refresh;
  const personalSpace = spacesData.data.personal;
  const otherSpaces = spacesData.data.spaces;
  const friendsData = useFriends();
  const visibleDmXid = view.kind === "home" && view.tab === "dm" ? activeDmXid : null;
  const dmsData = useDms(visibleDmXid);
  const refreshDms = dmsData.refresh;
  const channelsData = useChannels(
    view.kind === "space" ? view.spaceXid : null,
  );

  // Resolve the data the layout needs based on the current view.
  let activeSpace: SpaceView;
  let channels: ChannelView[];
  let activeChannel: ChannelView | undefined;
  if (view.kind === "home") {
    activeSpace = {
      xid: "home",
      spaceType: "personal_workspace",
      name: "Friends",
      iconLabel: "F",
      iconUrl: null,
    };
    channels = [];
    activeChannel = undefined;
  } else {
    const space =
      view.spaceXid === personalSpace.xid
        ? personalSpace
        : (otherSpaces.find((s) => s.xid === view.spaceXid) ?? personalSpace);
    activeSpace = space;
    channels = channelsData.data;
    activeChannel =
      channels.find((c) => c.xid === view.channelXid) ?? channels[0];
  }

  // ---- handlers ----------------------------------------------------------

  const goHome = (tab: HomeTab = "friends"): void => {
    setLastHomeTab(tab);
    setView({ kind: "home", tab });
    setMobileNavOpen(false);
  };

  const goSpace = (spaceXid: string): void => {
    const target =
      spaceXid === personalSpace.xid
        ? personalSpace
        : (otherSpaces.find((s) => s.xid === spaceXid) ?? personalSpace);
    // First channel is loaded lazily by useChannels; we set the
    // channelXid to "" until the response lands and the resolver
    // above picks the first.
    setView({ kind: "space", spaceXid: target.xid, channelXid: "" });
    setMobileNavOpen(false);
  };

  const selectChannel = (channelXid: string): void => {
    if (view.kind !== "space") return;
    setView({ ...view, channelXid });
    setMobileNavOpen(false);
  };

  const selectDm = (xid: string): void => {
    // useDms marks read only after the conversation is visible and focused.
    setActiveDmXid(xid);
    // Returning from a server must switch surfaces, not only change the DM id.
    setLastHomeTab("dm");
    setView({ kind: "home", tab: "dm" });
    setMobileNavOpen(false);
  };

  // ---- render ------------------------------------------------------------

  const isHome = view.kind === "home";
  const isPersonal =
    view.kind === "space" && activeSpace.xid === personalSpace.xid;

  useEffect(() => {
    if (view.kind === "space" && view.spaceXid === personalSpace.xid) {
      setView({ kind: "home", tab: "friends" });
    }
  }, [view, personalSpace.xid]);

  const selfForViews = {
    name: self.identity.name,
    tag: self.identity.tag,
    accountXid: self.identity.accountXid,
  };

  const startDm = useCallback(
    async (
      accountXid: string,
      draft?: string,
      sendImmediately = false,
    ): Promise<void> => {
      setDmError(null);
      try {
        const dm = await api.dms.create({ participant_xids: [accountXid] });
        if (draft !== undefined) {
          setDmDrafts((current) => ({
            ...current,
            [dm.xid]: { body: draft, sendImmediately },
          }));
        }
        setActiveDmXid(dm.xid);
        setLastHomeTab("dm");
        setView({ kind: "home", tab: "dm" });
        refreshDms();
      } catch (reason) {
        setDmError(
          reason instanceof Error
            ? reason.message
            : "Couldn't open that direct message.",
        );
      }
    },
    [refreshDms],
  );

  useEffect(() => {
    if (
      inviteToken === null ||
      readSessionToken() === null ||
      handledInvite.current === inviteToken
    ) {
      return;
    }
    handledInvite.current = inviteToken;
    setInviteMessage("Joining server…");
    void api.spaces
      .acceptInvite(inviteToken)
      .then(({ space }) => {
        refreshSpaces();
        setView({ kind: "space", spaceXid: space.xid, channelXid: "" });
        window.history.replaceState(null, "", "/");
        setInviteMessage(`Joined ${space.name}.`);
      })
      .catch((reason: unknown) => {
        setInviteMessage(
          reason instanceof Error
            ? reason.message
            : "That invite couldn't be accepted.",
        );
      });
  }, [inviteToken, refreshSpaces]);

  useEffect(() => {
    const onOpenDirectMessage = (event: Event): void => {
      const { accountXid, draft, sendImmediately } = (
        event as CustomEvent<OpenDirectMessageDetail>
      ).detail;
      void startDm(accountXid, draft, sendImmediately);
    };
    window.addEventListener(OPEN_DIRECT_MESSAGE_EVENT, onOpenDirectMessage);
    return () =>
      window.removeEventListener(OPEN_DIRECT_MESSAGE_EVENT, onOpenDirectMessage);
  }, [startDm]);

  useEffect(() => {
    const openSpace = (event: Event): void => { const detail = (event as CustomEvent<{ spaceXid?: unknown }>).detail; if (typeof detail.spaceXid === "string") { refreshSpaces(); setView({ kind: "space", spaceXid: detail.spaceXid, channelXid: "" }); setMobileNavOpen(false); } };
    window.addEventListener("link:open-space", openSpace);
    return () => window.removeEventListener("link:open-space", openSpace);
  });

  useEffect(() => {
    const openCallDm = (event: Event): void => {
      const dmXid = (event as CustomEvent<{ dmXid?: unknown }>).detail?.dmXid;
      if (typeof dmXid === "string") selectDm(dmXid);
    };
    window.addEventListener("link:open-dm-xid", openCallDm);
    return () => window.removeEventListener("link:open-dm-xid", openCallDm);
  });

  const signedOut = readSessionToken() === null;

  if (signedOut) {
    return (
      <TooltipProvider delayDuration={150}>
        <div className="flex h-screen w-screen items-center justify-center overflow-hidden bg-app-canvas">
          <div className="max-w-md px-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-strawberry-gradient text-white shadow-xl shadow-strawberry-300/40">
              <Sparkles className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-3xl font-semibold text-cloud-800">Welcome to Link</h1>
            <p className="mt-2 text-sm leading-relaxed text-cloud-500">
              Sign in with your device, or create an account to get started.
            </p>
          </div>
          <AuthScreen
            open={authOpen}
            onOpenChange={(open) => setAuthOpen(open || true)}
            onSession={() => window.location.reload()}
          />
        </div>
      </TooltipProvider>
    );
  }

  const navigationSidebar = isHome ? (
    <ChannelSidebar
      mobile
      mode="home"
      homeTab={view.tab}
      onSelectHomeTab={(tab) => goHome(tab)}
      dms={dmsData.data}
      friends={friendsData.data.friends}
      friendCount={friendsData.data.friends.length}
      activeDmXid={activeDmXid}
      onSelectDm={selectDm}
      onDmsChanged={refreshDms}
      onFriendsChanged={friendsData.refresh}
      onDmClosed={(dmXid) => {
        if (activeDmXid === dmXid) goHome("friends");
        refreshDms();
        friendsData.refresh();
      }}
      onGroupCreated={(dmXid) => { setActiveDmXid(dmXid); setLastHomeTab("dm"); setView({ kind: "home", tab: "dm" }); }}
    />
  ) : !isPersonal ? (
    <ChannelSidebar
      mobile
      mode="space"
      space={activeSpace}
      channels={channels}
      activeChannelXid={activeChannel?.xid}
      onSelectChannel={selectChannel}
      onSpaceChanged={spacesData.refresh}
      onSpaceDeleted={() => {
        goHome("friends");
        spacesData.refresh();
      }}
    />
  ) : null;

  return (
    <TooltipProvider delayDuration={150}>
      <DesktopStaffApproval />
      <div className="app-shell flex h-screen h-[100dvh] w-full overflow-hidden bg-app-canvas text-cloud-700">
        <div
          className={cn(
            "fixed inset-0 z-[140] lg:static lg:z-auto lg:flex lg:flex-none",
            mobileNavOpen ? "flex" : "hidden lg:flex",
          )}
          aria-hidden={!mobileNavOpen ? undefined : false}
        >
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm lg:hidden"
            aria-label="Close navigation"
          />
          <div className="relative z-10 flex h-full w-[min(100vw,380px)] max-w-full shadow-2xl lg:w-auto lg:shadow-none">
            <ServerRail
              mobile
              homeActive={isHome}
              dmUnreadCount={dmsData.data.reduce(
                (total, dm) => total + (dm.muted ? 0 : dm.unreadCount),
                0,
              )}
              spaces={otherSpaces}
              activeSpaceXid={
                view.kind === "space" ? view.spaceXid : null
              }
              onHomeClick={() => goHome("friends")}
              onSpaceClick={goSpace}
              onSpacesChanged={spacesData.refresh}
              onSpaceRemoved={() => {
                goHome("friends");
                spacesData.refresh();
              }}
            />
            {navigationSidebar}
          </div>
        </div>

        <div
          key={
            isPersonal
              ? "corner"
              : view.kind === "home"
                ? view.tab === "dm"
                  ? `home-dm-${activeDmXid ?? "none"}`
                  : `home-${view.tab}`
                : view.spaceXid
          }
          className="relative flex min-w-0 flex-1 animate-view-enter pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0"
        >
        {isHome ? (
          <>
            <FriendsView
              tab={view.tab}
              onSelectTab={(tab) => goHome(tab)}
              self={selfForViews}
              friends={friendsData.data.friends}
              pending={friendsData.data.pending}
              blocked={friendsData.data.blocked}
              dms={dmsData.data}
              activeDmXid={activeDmXid}
              onSelectDm={selectDm}
              onStartDm={(accountXid) => void startDm(accountXid)}
              initialDmDraft={
                activeDmXid === null
                  ? undefined
                  : dmDrafts[activeDmXid]?.body
              }
              sendInitialDmDraft={
                activeDmXid !== null &&
                dmDrafts[activeDmXid]?.sendImmediately === true
              }
              onConsumeDmDraft={(dmXid) =>
                setDmDrafts((current) => {
                  const next = { ...current };
                  delete next[dmXid];
                  return next;
                })
              }
              onFriendsChanged={friendsData.refresh}
              onDmsChanged={dmsData.refresh}
            />
          </>
        ) : isPersonal ? (
          // Keep stored personal data intact, but do not render My Corner,
          // including through an old bookmarked space/channel link.
          <main className="flex flex-1 items-center justify-center">
            <button type="button" onClick={() => goHome(lastHomeTab)} className="rounded-xl bg-white px-5 py-3 text-sm text-cloud-700">
              My Corner is hidden. Return to chats
            </button>
          </main>
        ) : (
          <>
            <MainPane
              space={activeSpace}
              channel={activeChannel}
              showMembers={showMembers}
              onToggleMembers={() => setShowMembers((v) => !v)}
            />
            {showMembers ? (
              <>
                <div className="hidden xl:block">
                  <MemberList space={activeSpace} />
                </div>
                <div className="fixed inset-0 z-[150] flex justify-end xl:hidden">
                  <button
                    type="button"
                    className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
                    onClick={() => setShowMembers(false)}
                    aria-label="Close member list"
                  />
                  <div className="relative z-10 h-full w-[min(20rem,calc(100vw-2rem))] max-w-full shadow-2xl">
                    <button
                      type="button"
                      onClick={() => setShowMembers(false)}
                      className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-20 flex h-9 w-9 items-center justify-center rounded-xl border border-cloud-200 bg-white/90 text-cloud-600 shadow-md backdrop-blur"
                      aria-label="Close member list"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <MemberList space={activeSpace} />
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}
        </div>

        <nav className="fixed inset-x-0 bottom-0 z-[130] grid h-[calc(4rem+env(safe-area-inset-bottom))] grid-cols-3 border-t border-cloud-200 bg-white/95 px-2 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_30px_rgba(36,39,51,0.08)] backdrop-blur-xl lg:hidden" aria-label="Mobile navigation">
          <button type="button" onClick={() => setMobileNavOpen(true)} className="flex flex-col items-center justify-center gap-1 text-[10px] font-semibold text-cloud-500 active:text-strawberry-600">
            <Menu className="h-5 w-5" />Browse
          </button>
          <button type="button" onClick={() => goHome("friends")} className={cn("flex flex-col items-center justify-center gap-1 text-[10px] font-semibold", isHome ? "text-strawberry-600" : "text-cloud-500")}>
            <Users className="h-5 w-5" />Friends
          </button>
          <button type="button" onClick={() => self.setProfileOpen(true)} className="flex flex-col items-center justify-center gap-1 text-[10px] font-semibold text-cloud-500 active:text-strawberry-600">
            <UserRound className="h-5 w-5" />Profile
          </button>
        </nav>

        <ProfileModal
          open={self.profileOpen}
          onOpenChange={self.setProfileOpen}
        />
        <SettingsModal
          open={self.settingsOpen}
          onOpenChange={self.setSettingsOpen}
        />
        <AuthScreen
          open={authOpen}
          onOpenChange={setAuthOpen}
          onSession={(info) => {
            self.patchIdentity({ accountXid: info.accountXid });
            // The data hooks read the session token once on mount;
            // after a fresh sign-in we need to refresh so they
            // pivot from demo placeholders to live API data.
            window.location.reload();
          }}
        />
        <SearchDialog
          open={searchOpen}
          onOpenChange={setSearchOpen}
          onNavigate={(spaceXid, channelXid) => {
            setView({ kind: "space", spaceXid, channelXid });
          }}
        />
        <UploadToasts />
        <ReleaseChangelog />
        {dmError !== null ? (
          <div className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] left-1/2 z-[180] flex w-[min(28rem,calc(100vw-1.5rem))] -translate-x-1/2 items-center gap-3 rounded-2xl border border-red-200 bg-white px-4 py-3 text-sm text-red-700 shadow-2xl lg:bottom-5 lg:w-auto">
            <AlertCircle className="h-4 w-4 flex-none" />
            <span className="min-w-0 flex-1">{dmError}</span>
            <button
              type="button"
              onClick={() => setDmError(null)}
              className="rounded-lg p-1 text-red-500 hover:bg-red-50"
              aria-label="Dismiss direct message error"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        {inviteMessage !== null ? (
          <div className="fixed left-1/2 top-[max(0.75rem,env(safe-area-inset-top))] z-[180] flex w-[min(24rem,calc(100vw-1.5rem))] -translate-x-1/2 items-center gap-3 rounded-2xl border border-cloud-300 bg-white px-4 py-3 text-sm text-cloud-700 shadow-2xl sm:left-auto sm:right-5 sm:top-5 sm:w-auto sm:translate-x-0">
            <Sparkles className="h-4 w-4 flex-none text-strawberry-500" />
            <span className="min-w-0 flex-1">{inviteMessage}</span>
            <button
              type="button"
              onClick={() => setInviteMessage(null)}
              className="rounded-lg p-1 text-cloud-400 hover:bg-cloud-100 hover:text-cloud-700"
              aria-label="Dismiss invite status"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        <MediaDock inlineDmXid={visibleDmXid} />
      </div>
    </TooltipProvider>
  );
}
